import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomFrom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function formatCoeffSqrt3(coeff) {
  if (coeff === 1) return "\\sqrt{3}";
  return `${coeff}\\sqrt{3}`;
}

function getPrismTypeLabel(baseKind) {
  if (baseKind === "square") return "czworokątny";
  if (baseKind === "equilateral") return "trójkątny";
  return "sześciokątny";
}

function getAreaFormula(baseKind) {
  if (baseKind === "square") return "P_p=a^2";
  if (baseKind === "equilateral") return "P_p=\\frac{a^2\\sqrt{3}}{4}";
  return "P_p=\\frac{3a^2\\sqrt{3}}{2}";
}

export function generateTask() {
  const baseKind = randomFrom(["square", "equilateral", "hexagon"]);
  const unknown = randomFrom(["a", "h"]);
  const h = randInt(2, 12);

  if (baseKind === "square") {
    const a = randInt(2, 14);
    const volume = a * a * h;
    return {
      type: "InverseRegularPrismDimensionTask",
      data: {
        baseKind,
        unknown,
        a,
        h,
        volume,
        hasSqrt: false,
        expected: unknown === "a" ? a : h,
      },
    };
  }

  // Dobieramy parzyste a, aby przy polu podstawy z sqrt(3) współczynniki były ładne.
  const a = randomFrom([2, 4, 6, 8, 10, 12]);

  if (baseKind === "equilateral") {
    // V = (a^2*sqrt(3)/4)*H = (a^2*H/4)*sqrt(3)
    const volumeCoeff = (a * a * h) / 4;
    return {
      type: "InverseRegularPrismDimensionTask",
      data: {
        baseKind,
        unknown,
        a,
        h,
        volumeCoeff,
        hasSqrt: true,
        expected: unknown === "a" ? a : h,
      },
    };
  }

  // hexagon: V = (3a^2*sqrt(3)/2)*H = (3a^2*H/2)*sqrt(3)
  const volumeCoeff = (3 * a * a * h) / 2;
  return {
    type: "InverseRegularPrismDimensionTask",
    data: {
      baseKind,
      unknown,
      a,
      h,
      volumeCoeff,
      hasSqrt: true,
      expected: unknown === "a" ? a : h,
    },
  };
}

export function renderTask({ data }) {
  const prismType = getPrismTypeLabel(data.baseKind);
  const unknownLabel = data.unknown === "a" ? "a" : "H";
  const givenPhrase =
    data.unknown === "a"
      ? `o danej objętości i danej wysokości`
      : `o danej objętości i danej krawędzi podstawy`;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        Dany jest graniastosłup prawidłowy {prismType} {givenPhrase}.
      </p>
      {data.hasSqrt ? (
        <BlockMath math={`V=${formatCoeffSqrt3(data.volumeCoeff)}\\,\\text{cm}^3`} />
      ) : (
        <BlockMath math={`V=${data.volume}\\,\\text{cm}^3`} />
      )}
      {data.unknown === "a" ? (
        <BlockMath math={`H=${data.h}\\,\\text{cm}`} />
      ) : (
        <BlockMath math={`a=${data.a}\\,\\text{cm}`} />
      )}
      <p>
        Oblicz {unknownLabel === "a" ? "krawędź podstawy" : "wysokość"}.
      </p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const label = task?.data?.unknown === "a" ? "a" : "H";
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={label}
      suffixLatex={String.raw`\text{cm}`}
      placeholder="np. 6"
    />
  );
}

export function checkAnswer(task, userAnswer) {
  const expected = task?.data?.expected;
  const parsed =
    userAnswer && typeof userAnswer === "object" ? userAnswer.parsed : NaN;
  if (!Number.isFinite(parsed)) return { correct: false };
  return { correct: Math.abs(parsed - expected) < 0.01 };
}

export function renderAnswer({ data }) {
  const label = data.unknown === "a" ? "a" : "H";
  return <InlineMath math={`${label}=${data.expected}\\,\\text{cm}`} />;
}

export function renderUserAnswer(userAnswer, task) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  const label = task?.data?.unknown === "a" ? "a" : "H";
  return (
    <InlineMath
      math={`${formatLatexPreview(userAnswer.raw, label)}\\,\\text{cm}`}
    />
  );
}

export function renderExplanation({ data }) {
  const { baseKind, unknown, a, h, expected } = data;
  const areaFormula = getAreaFormula(baseKind);

  if (!data.hasSqrt) {
    if (unknown === "h") {
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1:</strong> Liczymy pole podstawy.</p>
          <BlockMath math={`${areaFormula}=${a}^2=${a * a}\\,\\text{cm}^2`} />
          <p><strong>Krok 2:</strong> Korzystamy ze wzoru <InlineMath math={"V=P_p\\cdot H"} />.</p>
          <BlockMath math={`H=\\frac{V}{P_p}=\\frac{${data.volume}}{${a * a}}=${expected}\\,\\text{cm}`} />
        </div>
      );
    }
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1:</strong> Korzystamy ze wzoru <InlineMath math={"V=a^2\\cdot H"} />.</p>
        <BlockMath math={`a^2=\\frac{V}{H}=\\frac{${data.volume}}{${h}}=${a * a}`} />
        <BlockMath math={`a=\\sqrt{${a * a}}=${expected}\\,\\text{cm}`} />
      </div>
    );
  }

  if (baseKind === "equilateral") {
    if (unknown === "h") {
      const ppCoeff = (a * a) / 4;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1:</strong> Liczymy pole podstawy.</p>
          <BlockMath
            math={`P_p=\\frac{a^2\\sqrt{3}}{4}=\\frac{${a}^2\\sqrt{3}}{4}=${ppCoeff}\\sqrt{3}\\,\\text{cm}^2`}
          />
          <p><strong>Krok 2:</strong> Podstawiamy do wzoru <InlineMath math={"V=P_p\\cdot H"} /> i obliczamy <InlineMath math={"H"} />.</p>
          <BlockMath math={`${formatCoeffSqrt3(data.volumeCoeff)}=${ppCoeff}\\sqrt{3}\\cdot H\\ \\mid :\\sqrt{3}`} />
          <BlockMath math={`${data.volumeCoeff}=${ppCoeff}H`} />
          <BlockMath math={`H=\\frac{${data.volumeCoeff}}{${ppCoeff}}=${expected}\\,\\text{cm}`} />
        </div>
      );
    }
    const ppCoeff = data.volumeCoeff / h;
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1:</strong> Liczymy pole podstawy.</p>
        <BlockMath math={`P_p=\\frac{V}{H}=\\frac{${formatCoeffSqrt3(data.volumeCoeff)}}{${h}}=${ppCoeff}\\sqrt{3}\\,\\text{cm}^2`} />
        <p><strong>Krok 2:</strong> Korzystamy ze wzoru na pole podstawy trójkąta równobocznego i obliczamy <InlineMath math={"a"} />.</p>
        <BlockMath math={`${ppCoeff}\\sqrt{3}=\\frac{a^2\\sqrt{3}}{4}\\ \\mid :\\sqrt{3}`} />
        <BlockMath math={`${ppCoeff}=\\frac{a^2}{4}`} />
        <BlockMath math={`a^2=4\\cdot ${ppCoeff}=${a * a}`} />
        <BlockMath math={`a=\\sqrt{${a * a}}=${expected}\\,\\text{cm}`} />
      </div>
    );
  }

  if (unknown === "h") {
    const ppCoeff = (3 * a * a) / 2;
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <p><strong>Krok 1:</strong> Liczymy pole podstawy.</p>
        <BlockMath
          math={`P_p=\\frac{3a^2\\sqrt{3}}{2}=\\frac{3\\cdot ${a}^2\\sqrt{3}}{2}=${ppCoeff}\\sqrt{3}\\,\\text{cm}^2`}
        />
        <p><strong>Krok 2:</strong> Podstawiamy do wzoru <InlineMath math={"V=P_p\\cdot H"} /> i obliczamy <InlineMath math={"H"} />.</p>
        <BlockMath math={`${formatCoeffSqrt3(data.volumeCoeff)}=${ppCoeff}\\sqrt{3}\\cdot H\\ \\mid :\\sqrt{3}`} />
        <BlockMath math={`${data.volumeCoeff}=${ppCoeff}H`} />
        <BlockMath math={`H=\\frac{${data.volumeCoeff}}{${ppCoeff}}=${expected}\\,\\text{cm}`} />
      </div>
    );
  }

  const ppCoeff = data.volumeCoeff / h;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p><strong>Krok 1:</strong> Liczymy pole podstawy.</p>
      <BlockMath math={`P_p=\\frac{V}{H}=\\frac{${formatCoeffSqrt3(data.volumeCoeff)}}{${h}}=${ppCoeff}\\sqrt{3}\\,\\text{cm}^2`} />
      <p><strong>Krok 2:</strong> Korzystamy ze wzoru na pole podstawy sześciokąta foremnego i obliczamy <InlineMath math={"a"} />.</p>
      <BlockMath math={`${ppCoeff}\\sqrt{3}=\\frac{3a^2\\sqrt{3}}{2}\\ \\mid :\\sqrt{3}`} />
      <BlockMath math={`${ppCoeff}=\\frac{3a^2}{2}`} />
      <BlockMath math={`a^2=\\frac{2\\cdot ${ppCoeff}}{3}=${a * a}`} />
      <BlockMath math={`a=\\sqrt{${a * a}}=${expected}\\,\\text{cm}`} />
    </div>
  );
}
